import * as React from "react"
import {useState, useRef, useEffect} from "react"
import {Check, ChevronsUpDown, X} from "lucide-react"
import {cn} from "@/lib/utils"
import {Input} from "@/components/ui/input"
import {Button} from "@/components/ui/button"
import {useI18n} from "@/i18n"

export interface ComboboxOption {
    value: string
    label: string
    description?: string
}

interface ComboboxProps {
    options: ComboboxOption[]
    value: string | null
    onValueChange: (value: string | null) => void
    onSearch: (query: string) => void
    placeholder?: string
    emptyMessage?: string
    isLoading?: boolean
    className?: string
    disabled?: boolean
    /** Label to display when value is set but not in options (e.g., loaded from URL) */
    selectedLabel?: string
    'data-testid'?: string
}

export function Combobox({
    options,
    value,
    onValueChange,
    onSearch,
    placeholder,
    emptyMessage,
    isLoading = false,
    className,
    disabled = false,
    selectedLabel,
    'data-testid': testId,
}: ComboboxProps) {
    const i18n = useI18n()
    const [open, setOpen] = useState(false)
    const [inputValue, setInputValue] = useState("")
    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const resolvedPlaceholder = placeholder ?? i18n.combobox.searchPlaceholder
    const resolvedEmptyMessage = emptyMessage ?? i18n.combobox.noResults

    // Find selected option label
    const selectedOption = options.find((opt) => opt.value === value)

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value
        setInputValue(query)
        onSearch(query)
        setOpen(true)
    }

    // Handle option selection
    const handleSelect = (option: ComboboxOption) => {
        onValueChange(option.value)
        setInputValue("")
        setOpen(false)
    }

    // Handle clear
    const handleClear = () => {
        onValueChange(null)
        setInputValue("")
        onSearch("")
    }

    // Handle focus — fire search to preload initial results
    const handleFocus = () => {
        onSearch(inputValue)
        setOpen(true)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setOpen(false)
        }
    }

    const isOpen = open && !value
    return (
        <div ref={containerRef} className={cn("relative", className)} data-testid={testId} data-state={isOpen ? "open" : "closed"}>
            <div className="relative">
                {value ? (
                    // Show selected value
                    <div className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
                        <span className="truncate">{selectedOption?.label || selectedLabel || value}</span>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-muted"
                            onClick={handleClear}
                            disabled={disabled}
                            data-testid="btn-clear-selection"
                        >
                            <X className="h-4 w-4"/>
                            <span className="sr-only">{i18n.common.clear}</span>
                        </Button>
                    </div>
                ) : (
                    // Show search input
                    <>
                        <Input
                            ref={inputRef}
                            value={inputValue}
                            onChange={handleInputChange}
                            onFocus={handleFocus}
                            onKeyDown={handleKeyDown}
                            placeholder={resolvedPlaceholder}
                            disabled={disabled}
                            className="pr-8"
                        />
                        <ChevronsUpDown className="absolute right-3 top-3 h-4 w-4 shrink-0 opacity-50"/>
                    </>
                )}
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
                    <div className="max-h-60 overflow-auto p-1">
                        {isLoading ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                                {i18n.common.loading}
                            </div>
                        ) : options.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                                {inputValue ? resolvedEmptyMessage : i18n.combobox.typeToSearch}
                            </div>
                        ) : (
                            options.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    role="option"
                                    className={cn(
                                        "relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none",
                                        "hover:bg-accent hover:text-accent-foreground",
                                        "focus:bg-accent focus:text-accent-foreground"
                                    )}
                                    onClick={() => handleSelect(option)}
                                    data-testid={`btn-select-option-${option.value}`}
                                >
                                    <Check
                                        className={cn(
                                            "mr-2 h-4 w-4",
                                            value === option.value ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    <div className="flex flex-col items-start">
                                        <span>{option.label}</span>
                                        {option.description && (
                                            <span className="text-xs text-muted-foreground">
                                                {option.description}
                                            </span>
                                        )}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
